import { useEffect, useContext, useState } from "react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TextField,
  Button,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import ProfileQuestionContext from "../../../../../contexts/ProfileQuestionContext";

const RelatedQuestion = ({
  questionData,
  handleChange,
  handleMatchingAnswerChange,
  baseQuestion,
}) => {
  const { allQuestions } = useContext(ProfileQuestionContext);
  const [question, setQuestion] = useState(null);
  const [choices, setChoices] = useState(questionData.choices || []);
  const [choiceMapping, setChoiceMapping] = useState({});

  useEffect(() => {
    if (questionData?.questionType === "related") {
      setChoiceMapping(JSON.parse(questionData.matchingAnswer || "{}"));
    }
  }, [questionData]);

  useEffect(() => {
    if (questionData.questionType === "yesno") {
      setChoices(["yes", "no"]); // Corrected "now" to "no"
    } else {
      setChoices(questionData.choices || []);
    }
  }, [questionData]);

  const toCamelCase = (str) => {
    return str
      .toLowerCase()
      .replace(/[^a-zA-Z0-9]+(.)/g, (match, chr) => chr.toUpperCase());
  };

  const handleAddChoice = () => {
    setChoices((prev) => [...prev, { label: "", value: "" }]);
  };

  const handleChoiceChange = (index, value) => {
    const updatedChoices = [...choices];
    const camelCaseValue = toCamelCase(value);
    updatedChoices[index]["label"] = value;
    updatedChoices[index]["value"] = camelCaseValue;
    setChoices(updatedChoices);
    handleChange("choices", updatedChoices);
  };

  const handleMappingChange = (relatedChoice, value) => {
    const updatedMapping = {
      ...choiceMapping,
      [relatedChoice]: value,
    };
    setChoiceMapping(updatedMapping);
    handleChange("matchingAnswer", JSON.stringify(updatedMapping)); // Update the matching answer
  };

  useEffect(() => {
    if (questionData.relatedQuestion) {
      handleQuestion(questionData.relatedQuestion);
    }
  }, []);

  const handleQuestion = (id) => {
    const qst = allQuestions.filter((q) => q._id === id);
    setQuestion(qst[0]);
  };

  const renderHelp = () => {
    if (questionData.matchingCriteria === "different") {
      return `Example: If user answers question: "${questionData.question}" with a 'yes', and their date answers the question: "${question.question}" with a 'no', then they will match on this question. `;
    } else if (questionData.matchingCriteria === "same") {
      return `Example: If user answers question: "${questionData.question}" with a 'yes', and their date answers the question: "${question.question}" with a 'yes', then they will match on this question. `;
    } else if (
      question?.questionType === "yesno" &&
      (questionData.matchingCriteria !== "same" ||
        questionData.matchingCriteria !== "different")
    ) {
      return "The related question you have chosen is a Yes/No question. The matching criteria must be either 'same' or 'different'";
    }
  };

  if (["related"].includes(questionData.questionType)) {
    return (
      <Grid item xs={12}>
        <Grid container spacing={3}>
          {questionData.questionType === "related" ? (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Related Question</InputLabel>
                <Select
                  value={questionData.relatedQuestion || ""}
                  onChange={(e) => {
                    handleQuestion(e.target.value);
                    handleChange("relatedQuestion", e.target.value);
                  }}
                  disabled={baseQuestion}
                >
                  <MenuItem value="">Select Related Question</MenuItem>
                  {allQuestions.map((q) => (
                    <MenuItem key={q._id} value={q._id}>
                      {q.systemLabel ? q.systemLabel : q.question}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="caption" color="textSecondary">
                "Choose another question to which this answer affects"
              </Typography>
            </Grid>
          ) : (
            <></>
          )}

          {(questionData.matchingCriteria === "different" ||
            questionData.matchingCriteria === "multiple") && (
            <Grid
              item
              xs={12}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 20,
                border: "solid 1px black",
                borderRadius: 5,
                margin: 20,
                padding: 20,
              }}
            >
              <Typography variant="h6">Add Choices</Typography>
              {choices.map((choice, index) => (
                <Grid container spacing={2} key={index} alignItems="center">
                  <Grid item xs={5}>
                    <TextField
                      label={`Choice ${index + 1} Label`}
                      value={choice.label}
                      onChange={(e) =>
                        handleChoiceChange(index, e.target.value)
                      }
                      disabled={baseQuestion}
                      fullWidth
                    />
                  </Grid>
                  {/* Removed the 'Value' TextField as 'value' is auto-generated */}
                  <Grid item xs={2}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        const updatedChoices = choices.filter(
                          (_, idx) => idx !== index
                        );
                        setChoices(updatedChoices);
                        handleChange("choices", updatedChoices);
                      }}
                      disabled={baseQuestion}
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              ))}
              <Button
                variant="contained"
                onClick={handleAddChoice}
                style={{ width: 180 }}
                disabled={baseQuestion}
              >
                Add Choice
              </Button>

              {/* Choice Mapping Section */}
              <Typography variant="h6" sx={{ marginTop: 2 }}>
                Map Matching Answers
              </Typography>
              {questionData.relatedQuestion &&
                question?.choices.map((relatedChoice) => (
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    key={relatedChoice.value}
                  >
                    <Grid item xs={5}>
                      <Typography align="center">
                        Answering {relatedChoice.label} to "{question.question}"
                        will match if a person's date answers
                      </Typography>
                    </Grid>
                    {/* Removed the ArrowForwardIcon */}
                    <Grid item xs={2}>
                      <FormControl fullWidth>
                        <InputLabel>Map to</InputLabel>
                        <Select
                          disabled={baseQuestion}
                          multiple={
                            questionData.matchingCriteria === "multiple"
                          }
                          value={
                            questionData.matchingCriteria === "multiple"
                              ? Array.isArray(
                                  choiceMapping[relatedChoice.value]
                                )
                                ? choiceMapping[relatedChoice.value].filter(
                                    Boolean
                                  )
                                : []
                              : choiceMapping[relatedChoice.value] || ""
                          }
                          onChange={(e) =>
                            handleMappingChange(
                              relatedChoice.value,
                              e.target.value
                            )
                          }
                          renderValue={(selected) => {
                            if (questionData.matchingCriteria === "multiple") {
                              return Array.isArray(selected)
                                ? selected
                                    .map(
                                      (val) =>
                                        choices.find((c) => c.value === val)
                                          ?.label
                                    )
                                    .filter(Boolean)
                                    .join(", ")
                                : "";
                            } else {
                              // Single select
                              return (
                                choices.find((c) => c.value === selected)
                                  ?.label || ""
                              );
                            }
                          }}
                        >
                          {choices.map((choice) => (
                            <MenuItem key={choice.value} value={choice.value}>
                              {choice.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography> to "{questionData.question}"</Typography>
                    </Grid>
                  </Grid>
                ))}
            </Grid>
          )}

          {question?.questionType === "yesno" && (
            <Grid
              item
              xs={12}
              style={{
                border: "solid 1px black",
                borderRadius: 5,
                padding: 10,
                marginLeft: 16,
                marginTop: 16,
              }}
            >
              <Typography>{renderHelp()}</Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  }
  return <></>;
};

export default RelatedQuestion;
